import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { type Observable, filter, map, tap } from 'rxjs';

import { NavigationService } from '../navigation/navigation.service';
import { navLeaves, pathOf } from '../navigation/navigation.utils';
import { MissingProjects } from './missing-projects';
import type { Project, ProjectInput } from './project.model';
import { ProjectSelectionStore } from './project-selection.store';
import {
  PROJECTS_PATH,
  PROJECT_ID_PARAM,
  fillProjectRoute,
  projectIdFromUrl,
  projectScopedPath,
  workspacePathOf,
} from './project-scope';

/**
 * Where the Projects pane points: the project in the URL on a project page,
 * otherwise the remembered pick, otherwise the workspace's first project.
 *
 * Resolves the pane's `/projects/:projectId/…` templates and moves between
 * projects. It never feeds an API call — screens get their project from the
 * route through `ACTIVE_PROJECT_ID`.
 */
@Injectable({ providedIn: 'root' })
export class ProjectNavigationService {
  private readonly router = inject(Router);
  private readonly navigation = inject(NavigationService);
  private readonly store = inject(ProjectSelectionStore);
  private readonly missing = inject(MissingProjects);

  /** The project in the current URL; `''` when not on a project page. */
  readonly urlProjectId = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => projectIdFromUrl(event.urlAfterRedirects)),
    ),
    {
      initialValue: projectIdFromUrl(
        this.router.currentNavigation()?.finalUrl?.toString() ?? this.router.url,
      ),
    },
  );

  /** The project the pane's links lead to; `''` when the workspace has none. */
  readonly currentId = computed((): string => {
    const fromUrl = this.urlProjectId();

    if (fromUrl) {
      return fromUrl;
    }

    const projects = (this.store.projects() ?? []).filter(
      (project) => !this.missing.has(project.id),
    );
    const remembered = this.store.selectedProjectId();

    return projects.find((project) => project.id === remembered)?.id ?? projects[0]?.id ?? '';
  });

  readonly current = computed(
    (): Project | null =>
      this.store.projects()?.find((project) => project.id === this.currentId()) ?? null,
  );

  /** A pane route with the current project filled in; the landing page when there is none. */
  resolve(route: string): string {
    if (!route.includes(`:${PROJECT_ID_PARAM}`)) {
      return route;
    }

    const id = this.currentId();

    return id ? fillProjectRoute(route, id) : PROJECTS_PATH;
  }

  /** The first page the session may open in `projectId`, or null when no project app is on. */
  firstLink(projectId: string): string | null {
    const item = this.navigation.projectsItem();
    const first = item ? navLeaves(item)[0] : undefined;

    return first && projectId ? fillProjectRoute(first.route, projectId) : null;
  }

  /**
   * Remember `project`; on a project page, open the same page in it
   * (`/projects/a/tasker/tasks` → `/projects/b/tasker/tasks`).
   */
  switchTo(project: Project): void {
    this.store.select(project);

    const from = this.urlProjectId();

    if (from && from !== project.id) {
      const page = workspacePathOf(pathOf(this.router.url));

      void this.router.navigateByUrl(projectScopedPath(page === '/' ? '' : page, project.id));
    }
  }

  /** Create a project and switch to it. */
  create(input: ProjectInput): Observable<Project> {
    return this.store.create(input).pipe(tap((project) => this.switchTo(project)));
  }

  /** Rename / re-date a project; the open page stays where it is. */
  update(id: string, input: ProjectInput): Observable<Project> {
    return this.store.update(id, input);
  }

  /**
   * Delete a project, leaving its page for the landing page if it is open.
   * It is marked missing at once so nothing points at it while the list reloads.
   */
  remove(id: string): Observable<void> {
    return this.store.remove(id).pipe(
      tap(() => {
        this.missing.mark(id);
        this.leave(id);
      }),
    );
  }

  /** The backend no longer knows `id` (deleted elsewhere): drop it and leave its page. */
  forget(id: string): void {
    this.missing.mark(id);
    this.store.reload();
    this.leave(id);
  }

  private leave(id: string): void {
    if (this.urlProjectId() === id) {
      void this.router.navigateByUrl(PROJECTS_PATH);
    }
  }
}
